/* Blanc Space design search
 *
 * Search is also a model-aware browsing surface. Device state belongs to
 * window.BlancSpaceDevicePreference (see AGENTS.md); the localStorage access
 * below is compatibility fallback only. Do not introduce a second preference
 * or phone-list source here, and keep result links variant-specific.
 */
const PHONE_MODEL_STORAGE_KEY = 'blancspace.phoneModel';
const RECENT_SEARCH_STORAGE_KEY = 'blancspace.recentDesignSearches';
const POPULAR_FEED_CACHE = new Map();

class BlancSpaceDesignSearch extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.form = this.querySelector('[data-search-form]');
    this.input = this.querySelector('[data-search-input]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.modelSelect = this.querySelector('[data-phone-model]');
    this.body = this.querySelector('.bs-design-search__body');
    this.idle = this.querySelector('[data-search-idle]');
    this.loading = this.querySelector('[data-search-loading]');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.abortController = null;
    this.feedAbortController = null;
    this.popularAbortController = null;
    this.feedObserver = null;
    this.requestId = 0;
    this.popularRequestId = 0;
    this.cache = new Map();

    this.dedupeModelOptions();
    this.restorePhoneModel();
    this.renderRecentSearches();
    this.defaultIdleCards = Array.from(this.querySelectorAll('[data-idle-product]')).map((card) => card.cloneNode(true));
    this.prepareIdleFeed();

		// Resolve remembered-device cards only once the drawer is actually visible.
		// An unconditional init-time refresh fired a full storefront /search render
		// on every page load for every remembered-model visitor, competing with LCP
		// on pages where search is never opened. IntersectionObserver fires before
		// paint on the open frame, so the server-rendered iPhone defaults still
		// never remain visible by accident; the activeElement check covers a drawer
		// that autofocused the input before our focus listener attached.
		if (this.modelSelect?.value) {
			if (document.activeElement === this.input || this.getBoundingClientRect().height > 0) {
				queueMicrotask(() => this.ensurePopularDesigns());
			} else {
				this._visibilityObserver = new IntersectionObserver((entries) => {
					if (!entries.some((entry) => entry.isIntersecting)) return;
					this._visibilityObserver.disconnect();
					this._visibilityObserver = null;
					this.ensurePopularDesigns();
				});
				this._visibilityObserver.observe(this);
			}
		}

    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('focus', () => this.ensurePopularDesigns());
    this.form.addEventListener('reset', () => requestAnimationFrame(() => this.showIdle()));
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.modelSelect?.addEventListener('change', () => this.onModelChange());
    this.querySelector('[data-clear-recents]')?.addEventListener('click', () => this.clearRecentSearches());
    this.results.addEventListener('click', (event) => this.onResultsClick(event));
    this.querySelector('[data-recent-searches]')?.addEventListener('click', (event) => this.onRecentClick(event));
    this.addEventListener('click', (event) => this.onFeaturedProductClick(event));
    this._onSharedModelChange = (event) => {
      if (!this.modelSelect || this.modelSelect.value === event.detail?.model) return;
      this.modelSelect.value = event.detail?.model || '';
      if (this.getBoundingClientRect().height > 0) this.refreshPopularDesigns(this.modelSelect.value);
      if (this.input.value.trim()) this.search();
    };
    window.addEventListener('blancspace:phone-model-change', this._onSharedModelChange);

    this._debouncedSearch = this.debounce(() => this.search(), 260);
  }

  disconnectedCallback() {
    window.removeEventListener('blancspace:phone-model-change', this._onSharedModelChange);
    this._visibilityObserver?.disconnect();
    this._visibilityObserver = null;
    this.abortController?.abort();
    this.popularAbortController?.abort();
    this.resetProgressiveFeed();
    this.resetIdleFeed();
  }

  dedupeModelOptions() {
    if (!this.modelSelect) return;
    const seen = new Set();
    const blankOption = Array.from(this.modelSelect.options).find((option) => !option.value);
    const routeScore = (option) => option.value.toLowerCase().match(/[a-z0-9]+/g)?.filter((token) => (option.dataset.collectionUrl || '').toLowerCase().includes(token)).length || 0;
    const options = Array.from(this.modelSelect.options)
      .filter((option) => option.value)
      .sort((a, b) => {
        return (a.dataset.phoneModelSource === 'metafield' ? 0 : 1) - (b.dataset.phoneModelSource === 'metafield' ? 0 : 1) || routeScore(b) - routeScore(a);
      })
      .filter((option) => {
      const key = option.value.trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    const brandOrder = { apple: 0, samsung: 1, google: 2, other: 3 };
    const brandLabels = { apple: 'Apple', samsung: 'Samsung', google: 'Google', other: 'Other phones' };
    options.sort((a, b) => {
      const brandA = a.dataset.phoneBrand || 'other';
      const brandB = b.dataset.phoneBrand || 'other';
      if (brandA !== brandB) return (brandOrder[brandA] ?? 9) - (brandOrder[brandB] ?? 9);

      const generationA = this.phoneGeneration(a.value);
      const generationB = this.phoneGeneration(b.value);
      if (generationA !== generationB) return generationB - generationA;
      return this.phoneTier(a.value) - this.phoneTier(b.value) || a.value.localeCompare(b.value);
    });

    this.modelSelect.replaceChildren(blankOption || new Option('any phone', ''));
    Object.keys(brandOrder).forEach((brand) => {
      const brandOptions = options.filter((option) => (option.dataset.phoneBrand || 'other') === brand);
      if (!brandOptions.length) return;
      const group = document.createElement('optgroup');
      group.label = brandLabels[brand];
      group.append(...brandOptions);
      this.modelSelect.append(group);
    });
  }

  phoneGeneration(model) {
    if (/^iPhone Air$/i.test(model.trim())) return 17;
    const match = model.match(/(?:iPhone\s+|Galaxy\s+(?:Z\s+)?[A-Z]*|Pixel\s+)(\d+)/i)
      || model.match(/\bS(\d{2})\b/i);
    return match ? Number(match[1]) : 0;
  }

  phoneTier(model) {
    if (/pro max|ultra/i.test(model)) return 0;
    if (/\bpro\b/i.test(model)) return 1;
    if (/\bair\b|\bplus\b/i.test(model)) return 2;
    if (/\bflip\b|\bfold\b/i.test(model)) return 3;
    if (/\d+e\b/i.test(model)) return 5;
    return 4;
  }

  restorePhoneModel() {
    if (!this.modelSelect) return;
    const storedModel = this.getStoredPhoneModel();
    const matchingOption = Array.from(this.modelSelect.options).find((option) => option.value === storedModel);
    this.modelSelect.value = matchingOption ? storedModel : '';
  }

  getStoredPhoneModel() {
    if (window.BlancSpaceDevicePreference) return window.BlancSpaceDevicePreference.get();
    try {
      return localStorage.getItem(PHONE_MODEL_STORAGE_KEY) || '';
    } catch (error) {
      return '';
    }
  }

  onModelChange() {
    const model = this.modelSelect.value;
    if (window.BlancSpaceDevicePreference) {
      window.BlancSpaceDevicePreference.set(model, { source: 'search' });
    } else {
      try {
        if (model) localStorage.setItem(PHONE_MODEL_STORAGE_KEY, model);
        else localStorage.removeItem(PHONE_MODEL_STORAGE_KEY);
      } catch (error) {
        // Search still works when storage is unavailable.
      }
      window.dispatchEvent(new CustomEvent('blancspace:phone-model-change', { detail: { model, source: 'inferred' } }));
    }
    this.refreshPopularDesigns(model);
    if (this.input.value.trim()) this.search();
  }

  ensurePopularDesigns() {
    const model = this.modelSelect?.value || '';
    if ((this.dataset.popularModel || '') !== model) this.refreshPopularDesigns(model);
  }

  async refreshPopularDesigns(model) {
    const grid = this.querySelector('[data-idle-product-grid]');
    const feedHost = document.querySelector('[data-bs-search-feed-host]');
    if (!grid || !feedHost?.dataset.sectionId) return;

    const currentRequest = ++this.popularRequestId;
    this.popularAbortController?.abort();
    this.resetIdleFeed();

    if (!model) {
      grid.replaceChildren(...this.defaultIdleCards.map((card) => card.cloneNode(true)));
      Array.from(grid.children).forEach((card, index) => { card.hidden = index >= 6; });
      this.dataset.popularModel = '';
      this.prepareIdleFeed();
      return;
    }

    try {
      let cards = POPULAR_FEED_CACHE.get(model);
      if (!cards) {
        this.popularAbortController = new AbortController();
        const params = new URLSearchParams({
          q: `${model} ${this.dataset.caseType || 'Impact'}`,
          type: 'product',
          section_id: feedHost.dataset.sectionId
        });
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}search?${params}`, {
          signal: this.popularAbortController.signal,
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error(`Popular designs request failed: ${response.status}`);
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        cards = Array.from(parsed.querySelectorAll('[data-bs-popular-feed-product]')).map((card) => card.cloneNode(true));
        if (cards.length) POPULAR_FEED_CACHE.set(model, cards);
      }

      if (currentRequest !== this.popularRequestId || !cards?.length) return this.prepareIdleFeed();
      grid.replaceChildren(...cards.map((card) => card.cloneNode(true)));
      Array.from(grid.children).forEach((card, index) => { card.hidden = index >= 6; });
      this.dataset.popularModel = model;
      this.prepareIdleFeed();
      this.status.textContent = `Popular designs shown for ${model}.`;
    } catch (error) {
      if (error.name !== 'AbortError' && currentRequest === this.popularRequestId) this.prepareIdleFeed();
    }
  }

  onInput() {
    const hasQuery = this.input.value.trim().length > 0;
    this.clearButton.hidden = !hasQuery;
    if (!hasQuery) {
      this._debouncedSearch?.cancel?.();
      this.abortController?.abort();
      this.resetProgressiveFeed();
      this.showIdle();
      return;
    }
    this.showLoading();
    this._debouncedSearch();
  }

  async search() {
    const visibleQuery = this.input.value.trim();
    if (!visibleQuery) return this.showIdle();

    const contextualQuery = this.contextualiseQuery(visibleQuery);
    const cacheKey = contextualQuery.toLowerCase();
    const currentRequest = ++this.requestId;

    try {
      let data = this.cache.get(cacheKey);
      if (!data) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const params = new URLSearchParams({
          q: contextualQuery,
          'resources[type]': 'product,query,collection',
          'resources[limit]': '10',
          'resources[limit_scope]': 'each',
          'resources[options][unavailable_products]': 'hide',
          'resources[options][fields]': 'title,product_type,tag,variants.title'
        });
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}search/suggest.json?${params}`, {
          signal: this.abortController.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
        data = await response.json();
        this.cache.set(cacheKey, data);
      }
      if (currentRequest !== this.requestId) return;
      this.renderResults(data, visibleQuery, contextualQuery);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.renderError(visibleQuery);
    }
  }

  contextualiseQuery(query) {
    const model = this.modelSelect?.value || this.getStoredPhoneModel();
    if (!model) return query;

    const normalisedQuery = query.toLowerCase();
    const queryAlreadyNamesModel = Array.from(this.modelSelect?.options || []).some((option) => {
      const optionModel = option.value.trim().toLowerCase();
      return optionModel && normalisedQuery.includes(optionModel);
    });
    if (queryAlreadyNamesModel) return `${query} ${this.dataset.caseType || 'Impact'}`;
    return `${query} ${model} ${this.dataset.caseType || 'Impact'}`;
  }

  renderResults(data, visibleQuery, contextualQuery) {
    this.resetProgressiveFeed();
    const resources = data?.resources?.results || {};
    // Mystery items stay out of design results unless the shopper is looking for
    // them (query mentions mystery / surprise / bundle) - BLANC SPACE, 2 Nov 2026.
    const wantsMystery = /myster|surpris|bundle/.test((visibleQuery || '').toLowerCase());
    const products = (resources.products || []).filter((product) => {
      const type = (product.type || '').toLowerCase();
      const title = (product.title || '').toLowerCase();
      return (!type || type.includes('phone case')) && (wantsMystery || !title.includes('mystery'));
    }).slice(0, 8);
    const collections = (resources.collections || []).slice(0, 3);
    const queries = (resources.queries || []).slice(0, 5);

    this.results.replaceChildren();

    if (queries.length) {
      const section = this.createSection('Suggested searches');
      const chips = document.createElement('div');
      chips.className = 'bs-design-search__chips';
      queries.forEach((query) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'bs-design-search__chip';
        button.dataset.searchTerm = query.text;
        button.textContent = query.text;
        chips.append(button);
      });
      section.append(chips);
      this.results.append(section);
    }

    if (collections.length) {
      const section = this.createSection('Matching collections');
      const links = document.createElement('div');
      links.className = 'bs-design-search__collection-links';
      collections.forEach((collection) => {
        const link = document.createElement('a');
        link.href = collection.url;
        link.className = 'bs-design-search__collection-link';
        link.textContent = collection.title;
        links.append(link);
      });
      section.append(links);
      this.results.append(section);
    }

    if (products.length) {
      const section = this.createSection('Designs');
      const grid = document.createElement('div');
      grid.className = 'bs-design-search__product-grid';
      grid.dataset.searchProductGrid = '';
      products.forEach((product) => grid.append(this.createProductCard(product)));
      section.append(grid);
      this.results.append(section);
      this.prepareProgressiveFeed(grid, visibleQuery, contextualQuery);
    }

    if (!products.length && !collections.length && !queries.length) {
      const empty = document.createElement('div');
      empty.className = 'bs-design-search__empty';
      const title = document.createElement('h2');
      title.textContent = `No designs found for “${visibleQuery}”`;
      const text = document.createElement('p');
      text.textContent = 'Try a colour, pattern or broader style such as floral, cute or minimal.';
      empty.append(title, text);
      this.results.append(empty);
    } else {
      const viewAll = document.createElement('button');
      viewAll.type = 'submit';
      viewAll.className = 'button button--xl bs-design-search__view-all';
      viewAll.textContent = `View all results for “${visibleQuery}”`;
      this.results.append(viewAll);
    }

    this.idle.hidden = true;
    this.loading.hidden = true;
    this.results.hidden = false;
    this.status.textContent = `${products.length + collections.length + queries.length} suggestions found.`;
  }

  createSection(titleText) {
    const section = document.createElement('section');
    section.className = 'bs-design-search__section';
    const heading = document.createElement('div');
    heading.className = 'bs-design-search__section-heading';
    const title = document.createElement('h2');
    title.textContent = titleText;
    heading.append(title);
    section.append(heading);
    return section;
  }

  createProductCard(product) {
    const link = document.createElement('a');
    link.href = product.url;
    link.className = 'bs-search-product';
    link.dataset.searchProductKey = this.productKey(product.url);

    const imageWrap = document.createElement('span');
    imageWrap.className = 'bs-search-product__image';
    if (product.featured_image?.url) {
      const image = document.createElement('img');
      const imageUrl = new URL(product.featured_image.url, window.location.origin);
      imageUrl.searchParams.set('width', '500');
      image.src = imageUrl.toString();
      image.alt = product.featured_image.alt || product.title;
      image.loading = 'lazy';
      image.width = product.featured_image.width || 500;
      image.height = product.featured_image.height || 500;
      imageWrap.append(image);
    }

    const title = document.createElement('span');
    title.className = 'bs-search-product__title';
    title.textContent = product.title;
    link.append(imageWrap, title);
    return link;
  }

  productKey(url) {
    try {
      const parts = new URL(url, window.location.origin).pathname.split('/products/');
      return (parts[1] || url).split(/[/?#]/)[0];
    } catch (error) {
      return url;
    }
  }

  prepareProgressiveFeed(grid, visibleQuery, contextualQuery) {
    const feedHost = document.querySelector('[data-bs-search-feed-host]');
    if (!feedHost?.dataset.sectionId || !('IntersectionObserver' in window)) return;

    const controls = document.createElement('div');
    controls.className = 'bs-design-search__feed-controls';
    const loader = document.createElement('span');
    loader.className = 'bs-design-search__feed-loader';
    loader.hidden = true;
    loader.innerHTML = '<span aria-hidden="true"></span><span>Loading more designs…</span>';
    const sentinel = document.createElement('span');
    sentinel.className = 'bs-design-search__feed-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    controls.append(loader, sentinel);
    grid.closest('.bs-design-search__section').append(controls);

    this.feedState = {
      grid,
      loader,
      sentinel,
      sectionId: feedHost.dataset.sectionId,
      visibleQuery,
      contextualQuery,
      cards: null,
      loading: false,
      exhausted: false
    };
    this.feedObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.loadMoreDesigns();
    }, { root: this.body, rootMargin: '360px 0px' });
    this.feedObserver.observe(sentinel);
  }

  prepareIdleFeed() {
    this.resetIdleFeed();
    const grid = this.querySelector('[data-idle-product-grid]');
    const sentinel = this.querySelector('[data-idle-feed-sentinel]');
    const loader = this.querySelector('[data-idle-feed-loader]');
    const viewAll = this.querySelector('[data-idle-view-all]');
    if (!grid || !sentinel || !loader || !viewAll) return;

    sentinel.hidden = false;

    const hiddenCards = Array.from(grid.querySelectorAll('[data-idle-product][hidden]'));
    if (!hiddenCards.length || !('IntersectionObserver' in window)) {
      viewAll.hidden = false;
      sentinel.remove();
      return;
    }

    viewAll.hidden = true;
    this.idleFeedState = { grid, sentinel, loader, viewAll, loading: false };
    this.idleFeedObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.loadMoreIdleDesigns();
    }, { root: this.body, rootMargin: '360px 0px' });
    this.idleFeedObserver.observe(sentinel);
  }

  loadMoreIdleDesigns() {
    const state = this.idleFeedState;
    if (!state || state.loading || this.idle.hidden) return;
    const hiddenCards = Array.from(state.grid.querySelectorAll('[data-idle-product][hidden]'));
    if (!hiddenCards.length) return this.finishIdleFeed();

    state.loading = true;
    state.loader.hidden = false;
    this.idleFeedObserver?.unobserve(state.sentinel);
    requestAnimationFrame(() => {
      hiddenCards.slice(0, 8).forEach((card) => { card.hidden = false; });
      const shownCount = state.grid.querySelectorAll('[data-idle-product]:not([hidden])').length;
      this.status.textContent = `${shownCount} popular designs shown.`;
      state.loading = false;
      state.loader.hidden = true;
      if (state.grid.querySelector('[data-idle-product][hidden]')) {
        requestAnimationFrame(() => this.idleFeedObserver?.observe(state.sentinel));
      } else {
        this.finishIdleFeed();
      }
    });
  }

  finishIdleFeed() {
    const state = this.idleFeedState;
    if (!state) return;
    this.idleFeedObserver?.disconnect();
    state.loader.hidden = true;
    state.sentinel.hidden = true;
    state.viewAll.hidden = false;
  }

  resetIdleFeed() {
    this.idleFeedObserver?.disconnect();
    this.idleFeedObserver = null;
    this.idleFeedState = null;
  }

  async loadMoreDesigns() {
    const state = this.feedState;
    if (!state || state.loading || state.exhausted || state.grid.childElementCount >= 30) return;
    state.loading = true;
    state.loader.hidden = false;
    this.feedObserver?.unobserve(state.sentinel);

    try {
      if (!state.cards) {
        this.feedAbortController = new AbortController();
        const params = new URLSearchParams({
          q: state.contextualQuery,
          type: 'product',
          section_id: state.sectionId
        });
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}search?${params}`, {
          signal: this.feedAbortController.signal,
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error(`Search feed failed: ${response.status}`);
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        state.cards = Array.from(documentFragment.querySelectorAll('[data-search-feed-product]'));
      }

      const existingKeys = new Set(Array.from(state.grid.querySelectorAll('[data-search-product-key]')).map((card) => card.dataset.searchProductKey));
      const remaining = state.cards.filter((card) => !existingKeys.has(card.dataset.searchProductKey));
      const count = Math.min(8, 30 - state.grid.childElementCount);
      const batch = remaining.slice(0, count);
      batch.forEach((card) => state.grid.append(card));
      const appendedKeys = new Set(batch.map((card) => card.dataset.searchProductKey));
      state.cards = state.cards.filter((card) => !appendedKeys.has(card.dataset.searchProductKey));
      state.exhausted = !state.cards.length || !batch.length || state.grid.childElementCount >= 30;
      this.status.textContent = `${state.grid.childElementCount} designs shown for ${state.visibleQuery}.`;
    } catch (error) {
      if (error.name !== 'AbortError') state.exhausted = true;
    } finally {
      if (this.feedState !== state) return;
      state.loading = false;
      state.loader.hidden = true;
      if (!state.exhausted) requestAnimationFrame(() => this.feedObserver?.observe(state.sentinel));
      else state.sentinel.remove();
    }
  }

  resetProgressiveFeed() {
    this.feedAbortController?.abort();
    this.feedAbortController = null;
    this.feedObserver?.disconnect();
    this.feedObserver = null;
    this.feedState = null;
  }

  renderError(query) {
    this.resetProgressiveFeed();
    this.results.replaceChildren();
    const message = document.createElement('div');
    message.className = 'bs-design-search__empty';
    const title = document.createElement('h2');
    title.textContent = 'Search is taking a moment';
    const text = document.createElement('p');
    text.textContent = 'Press enter to see all search results.';
    message.append(title, text);
    this.results.append(message);
    this.idle.hidden = true;
    this.loading.hidden = true;
    this.results.hidden = false;
    this.status.textContent = `Live suggestions unavailable for ${query}.`;
  }

  showLoading() {
    this.resetIdleFeed();
    this.idle.hidden = true;
    this.results.hidden = true;
    this.loading.hidden = false;
  }

  showIdle() {
    this.resetProgressiveFeed();
    this.input.value = '';
    this.clearButton.hidden = true;
    this.results.hidden = true;
    this.loading.hidden = true;
    this.idle.hidden = false;
    this.status.textContent = '';
    const idleCards = Array.from(this.querySelectorAll('[data-idle-product]'));
    idleCards.forEach((card, index) => { card.hidden = index >= 6; });
    this.prepareIdleFeed();
  }

  onResultsClick(event) {
    const suggestion = event.target.closest('[data-search-term]');
    if (!suggestion) return;
    window.bsTrack?.('search_suggestion_clicked', { term: suggestion.dataset.searchTerm, surface: 'drawer' });
    this.input.value = suggestion.dataset.searchTerm;
    this.clearButton.hidden = false;
    this.search();
  }

  async onFeaturedProductClick(event) {
    const card = event.target.closest('[data-featured-product]');
    const model = this.modelSelect?.value || this.getStoredPhoneModel();
    if (!card || !model || card.dataset.resolving === 'true') return;

    event.preventDefault();
    card.dataset.resolving = 'true';
    const fallbackUrl = card.href;
    const title = card.dataset.productTitle;
    window.bsTrack?.('search_result_clicked', { title, query: this.input.value.trim().slice(0, 80), surface: 'drawer' });

    try {
      const params = new URLSearchParams({
        q: `${title} ${model} ${this.dataset.caseType || 'Impact'}`,
        'resources[type]': 'product',
        'resources[limit]': '10',
        'resources[options][unavailable_products]': 'hide',
        'resources[options][fields]': 'title,product_type,tag,variants.title'
      });
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}search/suggest.json?${params}`, {
        headers: { Accept: 'application/json' }
      });
      const products = (await response.json())?.resources?.results?.products || [];
      const exactProduct = products.find((product) => product.title.toLowerCase() === title.toLowerCase());
      window.location.assign(exactProduct?.url || fallbackUrl);
    } catch (error) {
      window.location.assign(fallbackUrl);
    }
  }

  onRecentClick(event) {
    const suggestion = event.target.closest('[data-search-term]');
    if (!suggestion) return;
    this.input.value = suggestion.dataset.searchTerm;
    this.clearButton.hidden = false;
    this.search();
  }

  onSubmit(event) {
    const query = this.input.value.trim();
    if (!query) return;
    event.preventDefault();
    this.saveRecentSearch(query);
    window.bsTrack?.('search_submitted', { query: query.slice(0, 80), surface: 'drawer' });
    // Submit the raw query: the submitted-search page resolves the remembered
    // model server-side per product, and appending "{model} Impact" here made
    // Shopify match every case in the catalogue (AGENTS.md Search contract).
    const params = new URLSearchParams({
      q: query,
      type: 'product',
      'options[prefix]': 'last'
    });
    window.location.assign(`${window.Shopify?.routes?.root || '/'}search?${params}`);
  }

  getRecentSearches() {
    try {
      return JSON.parse(localStorage.getItem(RECENT_SEARCH_STORAGE_KEY) || '[]');
    } catch (error) {
      return [];
    }
  }

  saveRecentSearch(query) {
    try {
      const searches = [query, ...this.getRecentSearches().filter((item) => item.toLowerCase() !== query.toLowerCase())].slice(0, 5);
      localStorage.setItem(RECENT_SEARCH_STORAGE_KEY, JSON.stringify(searches));
    } catch (error) {
      // Search navigation should never depend on storage.
    }
  }

  renderRecentSearches() {
    const section = this.querySelector('[data-recent-section]');
    const container = this.querySelector('[data-recent-searches]');
    if (!section || !container) return;
    const searches = this.getRecentSearches();
    section.hidden = searches.length === 0;
    container.replaceChildren(...searches.map((search) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bs-design-search__chip';
      button.dataset.searchTerm = search;
      button.textContent = search;
      return button;
    }));
  }

  clearRecentSearches() {
    try { localStorage.removeItem(RECENT_SEARCH_STORAGE_KEY); } catch (error) {}
    this.renderRecentSearches();
  }

  debounce(callback, delay) {
    let timeout;
    const debounced = (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => callback(...args), delay);
    };
    debounced.cancel = () => clearTimeout(timeout);
    return debounced;
  }
}

if (!customElements.get('bs-design-search')) {
  customElements.define('bs-design-search', BlancSpaceDesignSearch);
}
